/**
 * Emporium — Consent utilities (framework-free)
 *
 * Single source of truth for reading the visitor's cookie/tracking consent.
 * Used directly by analytics.js / cookies.js so those modules can gate
 * themselves without any React dependency. ConsentContext writes through
 * the same localStorage key, so hasConsent() always reflects the latest
 * choice made via the banner or preference centre.
 *
 * Categories: necessary (always true) | functional | analytics |
 *             personalisation | marketing
 */

export const POLICY_VERSION = "1.0"; // bump to re-prompt all visitors

const CONSENT_KEY = "hr_consent";
const VISITOR_ID_KEY = "hr_vid";

export const DEFAULT_CONSENT = {
  necessary: true,
  functional: false,
  analytics: false,
  personalisation: false,
  marketing: false,
};

/**
 * Long-lived pseudonymous visitor ID (independent of consent — generating
 * and storing this ID is itself a strictly-necessary function).
 */
export function getVisitorId() {
  try {
    let vid = localStorage.getItem(VISITOR_ID_KEY);
    if (!vid) {
      vid = (window.crypto && window.crypto.randomUUID)
        ? window.crypto.randomUUID()
        : `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`;
      localStorage.setItem(VISITOR_ID_KEY, vid);
    }
    return vid;
  } catch {
    return null;
  }
}

/**
 * Returns the stored consent object, or null if no decision has been
 * recorded yet, or if it was recorded under an older policy version
 * (forces a re-prompt).
 */
export function getStoredConsent() {
  try {
    const raw = localStorage.getItem(CONSENT_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (parsed.policyVersion !== POLICY_VERSION) return null;
    return parsed;
  } catch {
    return null;
  }
}

export function setStoredConsent(consent) {
  try {
    localStorage.setItem(CONSENT_KEY, JSON.stringify({ ...consent, policyVersion: POLICY_VERSION }));
  } catch {}
}

/**
 * Whether the given category is currently consented to.
 * "necessary" is always true (strictly necessary, locked on).
 */
export function hasConsent(category) {
  if (category === "necessary") return true;
  const consent = getStoredConsent();
  return !!(consent && consent[category]);
}
